import { takeLatest, call, put, fork, select } from 'redux-saga/effects';
import {
  DO_APPROVE_KYB,
  DO_REJECT_KYB,
  DO_SET_KYB_STATUS_PENDING,
  LOAD_KYB_BUSINESS_DETAIL,
  LOAD_KYB_BUSINESSES_LIST,
  SUBMIT_KYB_EDIT_BANK_ACCOUNT,
  SUBMIT_KYB_EDIT_KTP,
} from 'state/actionTypes';
import {
  setKybListIsLoading,
  setKybBusinessesList,
  setKybListFilters,
  setKybDetailModalShown,
  setKybDetailBusinessDetails,
  appendKybDetailErrorMessages,
  clearKybDetailErrorMessages,
  setKybBusinessDetailLoadingState,
  setKybDetailKtpDetails,
  setKybDetailBankAccount,
  setKybApproveConfirmModalLoading,
  setKybApproveConfirmModalShown,
  clearKybApprovalLogs,
  setKybApprovalLogs,
  loadKybBusinessesList as loadKybBusinessListAction,
  setKybRejectConfirmModalLoading,
  setKybRejectConfirmModalShown,
  setKybEditBankAccountModalLoading,
  setKybEditBankAccountModalShown,
  setKybReviewDetailsModalLoading,
  setKybReviewDetailsModalShown,
  setKybEditKtpModalLoading,
  setKybEditKtpModalShown,
} from 'state/kyb/actions';
import * as api from 'api/kyb-npwp';
import * as toast from 'state/toast/actions';
import {
  APPROVED,
  PENDING,
  REJECTED,
} from 'constants/kybNpwp/parameterConstants';

export function* loadKybBusinessesList({ payload }) {
  const { filters, paging } = payload;
  yield put(setKybListIsLoading(true));
  try {
    yield put(setKybListFilters(filters));
    const response = yield call(api.getBusinessesList, paging, filters);
    yield put(setKybBusinessesList(response.data.data, response.data.paging));
  } catch (error) {
    yield put(toast.errorMessage('Load businesses list failed', error.message));
  } finally {
    yield put(setKybListIsLoading(false));
  }
}

export function* resetKybBusinessDetailLoadingStates() {
  yield put(
    setKybBusinessDetailLoadingState({
      business: false,
      ktp: false,
      bankAccount: false,
      log: false,
    })
  );
}

export function* fetchBusinessDetail(businessId) {
  yield put(setKybDetailBusinessDetails(null));
  yield put(
    setKybBusinessDetailLoadingState({
      business: true,
    })
  );
  try {
    const { data: responseData } = yield call(
      api.getBusinessDetails,
      businessId
    );
    yield put(setKybDetailBusinessDetails(responseData.data));
  } catch (e) {
    yield put(
      appendKybDetailErrorMessages(e.message.map(m => `Business Detail: ${m}`))
    );
  } finally {
    yield put(
      setKybBusinessDetailLoadingState({
        business: false,
      })
    );
  }
}

export function* fetchKtpDetail(businessId) {
  yield put(setKybDetailKtpDetails(null));
  yield put(
    setKybBusinessDetailLoadingState({
      ktp: true,
    })
  );
  try {
    const { data: responseData } = yield call(
      api.getBusinessIdentities,
      businessId
    );
    const ktp = {
      number: responseData.data.KTP_NUMBER,
      name: responseData.data.KTP_NAME,
      image: responseData.data.IMAGE_URL,
    };
    yield put(setKybDetailKtpDetails(ktp));
  } catch (e) {
    yield put(appendKybDetailErrorMessages(e.message.map(m => `KTP: ${m}`)));
  } finally {
    yield put(
      setKybBusinessDetailLoadingState({
        ktp: false,
      })
    );
  }
}

export function* fetchDefaultBankAccount(businessId) {
  yield put(setKybDetailBankAccount(null));
  yield put(
    setKybBusinessDetailLoadingState({
      bankAccount: true,
    })
  );
  try {
    const { data: responseData } = yield call(
      api.getDefaultBusinessBankAccount,
      businessId
    );
    yield put(setKybDetailBankAccount(responseData.data));
  } catch (e) {
    yield put(
      appendKybDetailErrorMessages(e.message.map(m => `Bank Account: ${m}`))
    );
  } finally {
    yield put(
      setKybBusinessDetailLoadingState({
        bankAccount: false,
      })
    );
  }
}

export function* fetchKybLogs(businessId) {
  yield put(clearKybApprovalLogs());
  yield put(
    setKybBusinessDetailLoadingState({
      log: true,
    })
  );
  try {
    const { data: responseData } = yield call(api.getKybLogs, businessId);
    yield put(setKybApprovalLogs(responseData.data));
  } catch (e) {
    yield put(appendKybDetailErrorMessages(e.message.map(m => `Logs: ${m}`)));
  } finally {
    yield put(setKybBusinessDetailLoadingState({ log: false }));
  }
}

export function* loadKybBusinessDetail({ payload }) {
  const { businessId } = payload;
  yield put(clearKybDetailErrorMessages());
  yield call(resetKybBusinessDetailLoadingStates);
  yield put(setKybDetailModalShown(true));
  yield fork(fetchKtpDetail, businessId);
  yield fork(fetchDefaultBankAccount, businessId);

  yield call(fetchBusinessDetail, businessId);
  yield fork(fetchKybLogs, businessId);
}

export function* reloadKybList() {
  const {
    businesses: { data: paging, filters },
  } = yield select(state => state.kyb);
  yield put(loadKybBusinessListAction(filters, paging));
}

export function* doApproveKyb({ payload }) {
  const { businessId } = payload;
  yield put(clearKybDetailErrorMessages());
  yield put(setKybApproveConfirmModalLoading(true));
  try {
    yield call(api.updateKybStatus, businessId, APPROVED);
  } catch (e) {
    yield put(
      appendKybDetailErrorMessages(e.message.map(m => `Approval: ${m}`))
    );
  } finally {
    yield put(setKybApproveConfirmModalLoading(false));
    yield put(setKybApproveConfirmModalShown(false));
  }

  yield put(clearKybApprovalLogs());
  yield fork(reloadKybList);
  yield call(fetchBusinessDetail, businessId);
  yield call(fetchKybLogs, businessId);
}

export function* doRejectKyb({ payload }) {
  const { businessId, reason } = payload;
  yield put(clearKybDetailErrorMessages());
  yield put(setKybRejectConfirmModalLoading(true));
  try {
    yield call(api.updateKybStatus, businessId, REJECTED, reason);
  } catch (e) {
    yield put(
      appendKybDetailErrorMessages(e.message.map(m => `Rejection: ${m}`))
    );
  } finally {
    yield put(setKybRejectConfirmModalLoading(false));
    yield put(setKybRejectConfirmModalShown(false));
  }
  yield call(resetKybBusinessDetailLoadingStates);
  yield put(clearKybApprovalLogs());
  yield fork(reloadKybList);
  yield call(fetchBusinessDetail, businessId);
  yield call(fetchKybLogs, businessId);
}

export function* doEditBankAccount({ payload }) {
  const { businessId, form } = payload;
  yield put(setKybEditBankAccountModalLoading(true));
  yield put(clearKybDetailErrorMessages());
  try {
    yield call(api.updateBankAccount, businessId, form);
  } catch (e) {
    yield put(
      appendKybDetailErrorMessages(
        e.message.map(m => `Edit Bank Account: ${m}`)
      )
    );
  } finally {
    yield put(setKybEditBankAccountModalLoading(false));
    yield put(setKybEditBankAccountModalShown(false));
  }
  yield put(clearKybApprovalLogs());
  yield fork(fetchDefaultBankAccount, businessId);
  yield call(fetchKybLogs, businessId);
}

export function* doSetKybStatusPending({ payload }) {
  const { businessId } = payload;
  yield put(clearKybDetailErrorMessages());
  yield put(setKybReviewDetailsModalLoading(true));
  try {
    yield call(api.updateKybStatus, businessId, PENDING);
  } catch (e) {
    yield put(
      appendKybDetailErrorMessages(e.message.map(m => `Update Details: ${m}`))
    );
  } finally {
    yield put(setKybReviewDetailsModalLoading(false));
    yield put(setKybReviewDetailsModalShown(false));
  }
  yield put(clearKybApprovalLogs());
  yield fork(reloadKybList);
  yield call(fetchBusinessDetail, businessId);
  yield call(fetchKybLogs, businessId);
}

export function* doEditKtp({ payload }) {
  const { businessId, form } = payload;
  yield put(clearKybDetailErrorMessages());
  yield put(setKybEditKtpModalLoading(true));
  try {
    yield call(api.updateKtpDetails, businessId, form);
  } catch (e) {
    yield put(
      appendKybDetailErrorMessages(e.message.map(m => `Edit KTP: ${m}`))
    );
  } finally {
    yield put(setKybEditKtpModalLoading(false));
    yield put(setKybEditKtpModalShown(false));
  }
  yield put(clearKybApprovalLogs());
  yield fork(fetchKtpDetail, businessId);
  yield call(fetchKybLogs, businessId);
}

export default function*() {
  yield takeLatest(LOAD_KYB_BUSINESSES_LIST, loadKybBusinessesList);
  yield takeLatest(LOAD_KYB_BUSINESS_DETAIL, loadKybBusinessDetail);
  yield takeLatest(DO_APPROVE_KYB, doApproveKyb);
  yield takeLatest(DO_REJECT_KYB, doRejectKyb);
  yield takeLatest(SUBMIT_KYB_EDIT_BANK_ACCOUNT, doEditBankAccount);
  yield takeLatest(DO_SET_KYB_STATUS_PENDING, doSetKybStatusPending);
  yield takeLatest(SUBMIT_KYB_EDIT_KTP, doEditKtp);
}
